import { useEffect, useState } from 'react';
import type { Client } from '../../types/crm';
import { useClientLogoUrl } from '../../hooks/useClientLogoUrl';
import { clientInitials } from '../../lib/clientLogo';
import { isClientLogoImageReady, markClientLogoImageReady } from '../../lib/clientLogoCache';

interface AccountAvatarProps {
  client: Pick<Client, 'company_name' | 'logo_path'>;
  size?: 'sm' | 'lg';
  className?: string;
}

export function AccountAvatar({ client, size = 'sm', className = '' }: AccountAvatarProps) {
  const logoPath = client.logo_path?.trim() ?? '';
  const hasLogo = Boolean(logoPath);
  const logoUrl = useClientLogoUrl(hasLogo ? logoPath : null);
  const [imgFailed, setImgFailed] = useState(false);
  const [imgReady, setImgReady] = useState(() =>
    Boolean(logoUrl && isClientLogoImageReady(logoUrl)),
  );
  const initials = clientInitials(client.company_name);
  const showImage = hasLogo && Boolean(logoUrl) && !imgFailed;
  const showInitials = !hasLogo || imgFailed;

  useEffect(() => {
    setImgFailed(false);
    setImgReady(Boolean(logoUrl && isClientLogoImageReady(logoUrl)));
  }, [logoPath, logoUrl]);

  function handleImageLoad() {
    if (logoUrl) markClientLogoImageReady(logoUrl);
    setImgReady(true);
  }

  return (
    <div
      className={`account-avatar account-avatar--${size}${className ? ` ${className}` : ''}`}
      aria-hidden={size === 'sm'}
    >
      {showImage && logoUrl && (
        <img
          className={`account-avatar__img${imgReady ? ' account-avatar__img--ready' : ''}`}
          src={logoUrl}
          alt=""
          decoding="async"
          onLoad={handleImageLoad}
          onError={() => setImgFailed(true)}
        />
      )}
      <span
        className={`account-avatar__initials${showInitials ? '' : ' account-avatar__initials--hidden'}`}
      >
        {initials}
      </span>
    </div>
  );
}
